import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TextData } from '../../../shared/dto/EntityRepository';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { textBuilder } from '../../../shared/converter/entityBuilder';
import { useUnsavedChanges } from '../../../shared/hooks/useUnsavedChanges';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useTextListActions, useTextListState } from '../../../shared/hooks/useApi/useTextList';
import EmptyTextList from '../EmptyTextList';
import TextInfo from '../TextInfo';
import TextViewerMobile from './TextViewerMobile';
import { useTextListStore } from '../../../shared/store/TextListStore';
import { useTextViewStore } from '../../../shared/store/TextViewStore';
import createIcon from '../../../asset/icon/create.png';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import { bottomWithSafeArea } from '../../../shared/constants/safeArea';
import { usePageOwnerIdentityStore } from '../../../shared/store/pageOwnerIdentityStore';
import { useParams } from 'react-router-dom';
import { isPageOwnerMatchedToRaw } from '../../../shared/util/pageOwnerMatch';
import { canShowTextList } from '../../../shared/util/visibility/textListVisibility';
import type { ResourceScope } from '../../../shared/util/scopedResource';
import { useTextTagStore } from '../../../shared/store/textTagStore';
import { toTextListFilterData } from '../../../shared/util/textTag';

const TextListMobile: React.FC = () => {
  const { isEditMode } = useEditMode();
  const { isOwner } = useAuth();
  const { textList, listOwnerAui, listScope, status } = useTextListState();
  const {
    getTextList,
    getTextListOwner,
    getText,
    getTextOwner,
    createText,
    clearActiveText,
  } = useTextListActions();
  const pageSize = useTextListStore((s) => s.pageSize);
  const sortData = useTextListStore((s) => s.sortData);
  const selectedTags = useTextTagStore((s) => s.selectedTags);
  // Boolean only: avoid re-rendering the whole list on every activeText field update.
  const hasActiveText = useTextViewStore((s) => s.activeText != null);
  const [selectedId, setSelectedId] = useState<string>("");
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;
  const { runAfterUnsavedChangesCheck } = useUnsavedChanges();
  const { user } = useAuth();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { HANDLE: raw } = useParams<{ HANDLE?: string }>();
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowTextList({
    pageOwnerAui: pageOwner?.aui,
    listOwnerAui,
    listScope,
    currentScope,
    status,
  });
  const visibleTextList = canShowList ? textList : [];

  useEffect(() => {
    if (!hasActiveText) {
      setSelectedId("");
    }
  }, [hasActiveText]);

  useEffect(() => {
    const getTextListWithApi = async () => {
      if (!isPageOwnerMatchedToRaw(raw, pageOwner)) return;

      const filterData = toTextListFilterData(selectedTags);
      if (isOwner) {
        await getTextListOwner({ page: 1, size: pageSize, sortData: sortData, filterData });
      } else {
        await getTextList({ page: 1, size: pageSize, sortData: sortData, filterData });
      }
    }
    getTextListWithApi();
    /** 멀티 선택은 모바일에서 사용하지 않음 — 목록 새로 고침 시 스토어의 multi도 함께 초기화 */
    clearActiveText();
  }, [raw, pageOwner?.aui, pageSize, sortData, isOwner, selectedTags]);

  const handleCreateText = async () => {
    if (!user) return;
    runAfterUnsavedChangesCheck(async () => {
      await createText(textBuilder());
    });
  };

  const handleTextClick = useCallback((data: TextData) => {
    const newId = data.id;
    if (newId === selectedIdRef.current) return;

    runAfterUnsavedChangesCheck(() => {
      void (async () => {
        clearActiveText();
        if (isOwner) {
          await getTextOwner(newId);
        } else {
          await getText(newId);
        }
        setSelectedId(newId);
      })();
    });
  }, [clearActiveText, getText, getTextOwner, isOwner, runAfterUnsavedChangesCheck]);

  return (
    <TextListContainer>
      <TextListComp $isActive={hasActiveText}>
        {visibleTextList.map((each: TextData) =>
          <TextInfo
            key={each.id}
            data={each}
            handleTextClick={handleTextClick}
          />
        )}
        {canShowList && visibleTextList.length === 0 && <EmptyTextList />}
        {isEditMode &&
          <BtnContainer>
            <UniversalIconBtn 
              size="standard" 
              border={true} 
              backgroundImg={createIcon} 
              onClick={handleCreateText} />
          </BtnContainer>
        }
      </TextListComp>
      <TextViewerMobile />
    </TextListContainer>
  );
}
const TextListContainer = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  outline: none;
`;

const TextListComp = styled.section<{ $isActive: boolean }>`
  width: ${({ $isActive }) => $isActive ? '0%' : '100%'};
  opacity: ${({ $isActive }) => $isActive ? 0 : 1};
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  transition: all 0.7s ease-in-out;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: ${bottomWithSafeArea('clamp(30px, 7vw, 40px)')};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: 0.4vw;
`;


export default TextListMobile;
